import React, { useState, useEffect } from 'react';
import { checkUserPromotion, calculatePromotionPrice } from '../../../utils/promotionApi';
import styles from './PlanSelection.module.css';

const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

/**
 * PlanSelectionModal - Shows pricing plans with promotional badges
 * Checks for active promotions and displays them prominently on plan cards
 */
const PlanSelectionModal = ({ isOpen, onClose, onPlanSelected }) => {
  // One promotion object per plan — null means no active promo with slots for that plan.
  // Each is fetched separately so the backend checks THAT plan's slot count per row.
  const [promotions,        setPromotions]        = useState({ basic: null, pro: null, business: null });
  const [loading,           setLoading]           = useState(true);
  const [paymentAvailable,  setPaymentAvailable]  = useState(false);
  const [paymentCheckDone,  setPaymentCheckDone]  = useState(false);
  const [paymentBlockReason, setPaymentBlockReason] = useState('');   // shown in toast
  const [showPaymentToast,  setShowPaymentToast]  = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkAllPromotions();
      checkPaymentReadiness();
    }
  }, [isOpen]);

  // ── Smart two-stage payment readiness check ───────────────────────────────
  //
  // Stage 1 — Is the Django API reachable?
  //   Hits /api/ads/?page_size=1 — a real public endpoint.
  //   If this fails → server is down, payments impossible regardless of environment.
  //
  // Stage 2 — Can Daraja (M-Pesa) reach your callback URL?
  //   We detect the current environment from window.location.hostname:
  //
  //   LOCAL  (localhost / 127.x / 192.168.x / 10.x / private IPs):
  //     Daraja is a remote server and CANNOT reach your machine directly.
  //     Payments only work if ngrok (or similar tunnel) is active and registered
  //     with your Django backend as the callback URL.
  //     → We check /api/mpesa/callback-health/ which your Django view should return:
  //         { "callback_reachable": true/false, "tunnel_url": "..." }
  //       If that endpoint doesn't exist yet (404) → treat as unavailable.
  //       If ngrok is off → callback_reachable: false → buttons disabled.
  //
  //   LIVE   (any real public hostname — production / staging):
  //     Daraja can reach your server directly via HTTPS.
  //     No tunnel needed. If Stage 1 passed, payments are available.
  //
  // Result: paymentAvailable is only true when BOTH directions work.
  const checkPaymentReadiness = async () => {
    setPaymentCheckDone(false);
    setPaymentAvailable(false);
    setPaymentBlockReason('');

    // ── Stage 1: Is our Django API up? ──────────────────────────────────────
    let apiUp = false;
    try {
      const res = await fetch(`${API_BASE}/ads/?page_size=1`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      apiUp = res.ok;
    } catch {
      apiUp = false;
    }

    if (!apiUp) {
      setPaymentAvailable(false);
      setPaymentBlockReason('Payment server is unreachable. Please try again later.');
      setPaymentCheckDone(true);
      return;
    }

    // ── Stage 2: Can Daraja reach our callback? ──────────────────────────────
    const hostname = window.location.hostname;
    const isLocalEnv = (
      hostname === 'localhost'       ||
      hostname === '127.0.0.1'       ||
      /^192\.168\.\d+\.\d+$/.test(hostname) ||   // LAN
      /^10\.\d+\.\d+\.\d+$/.test(hostname)  ||   // LAN
      /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(hostname)  // LAN
    );

    if (!isLocalEnv) {
      // Live/production — Daraja can reach us directly. Stage 1 is sufficient.
      setPaymentAvailable(true);
      setPaymentBlockReason('');
      setPaymentCheckDone(true);
      return;
    }

    // Local environment — check if a tunnel (ngrok) is active and registered.
    // Your Django view at /api/mpesa/callback-health/ should return:
    //   { "callback_reachable": true, "tunnel_url": "https://xxxx.ngrok.io/..." }
    //   or
    //   { "callback_reachable": false, "reason": "No tunnel configured" }
    //
    // If the endpoint doesn't exist yet (404/500) we treat it as unavailable
    // so local payments stay disabled until you add it — safe default.
    try {
      const res = await fetch(`${API_BASE}/mpesa/callback-health/`, {
        method: 'GET',
        signal: AbortSignal.timeout(4000),
      });

      if (!res.ok) {
        // Endpoint not yet implemented or erroring — disable locally
        setPaymentAvailable(false);
        setPaymentBlockReason(
          'Running locally without a payment tunnel. Start ngrok and register it with your server to enable paid plans.'
        );
        setPaymentCheckDone(true);
        return;
      }

      const data = await res.json();
      const tunnelActive = data?.callback_reachable === true;

      setPaymentAvailable(tunnelActive);
      setPaymentBlockReason(
        tunnelActive
          ? ''
          : 'M-Pesa callback tunnel (ngrok) is not active. Start ngrok and register it with your server to enable paid plans.'
      );
    } catch {
      // Network error reaching the health endpoint — disable locally
      setPaymentAvailable(false);
      setPaymentBlockReason(
        'Could not verify M-Pesa callback tunnel. Start ngrok to enable paid plans locally.'
      );
    }

    setPaymentCheckDone(true);
  };

  const handleUnavailableClick = () => {
    setShowPaymentToast(true);
    setTimeout(() => setShowPaymentToast(false), 5000);
  };

  const checkAllPromotions = async () => {
    setLoading(true);
    try {
      // Call backend once per plan — backend picks the FIRST promo row that has
      // slots > 0 for that plan. This means:
      //   • each plan gets its own independent check
      //   • a user with 2 rows (e.g. Discounted + Re-Post) gets the first valid one
      //   • a plan with 0 slots across ALL rows correctly returns has_promotion:false
      const [basicRes, proRes, bizRes] = await Promise.all([
        checkUserPromotion('basic'),
        checkUserPromotion('pro'),
        checkUserPromotion('business'),
      ]);

      setPromotions({
        basic:    basicRes.has_promotion    ? basicRes.promotion    : null,
        pro:      proRes.has_promotion      ? proRes.promotion      : null,
        business: bizRes.has_promotion      ? bizRes.promotion      : null,
      });
    } catch (err) {
      console.error('[PlanSelectionModal] checkAllPromotions error:', err);
      setPromotions({ basic: null, pro: null, business: null });
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Free',
      icon: '👑',
      price: 0,
      period: '',
      features: [
        'Up to 2 Images',
        '14 Day Ad Display',
        'Totally free ad posting',
      ],
      maxImages: 2,
      maxVideos: 0,
      displayDays: 14,
      buttonText: 'Free',
      buttonColor: 'blue',
    },
    {
      id: 'basic',
      name: 'Basic',
      badge: 'POPULAR',
      price: 50,
      period: '/MO',
      features: [
        'Up to 5 Images',
        '1 Month Ad Display',
        'Increased ad visibility',
      ],
      maxImages: 5,
      maxVideos: 0,
      displayDays: 30,
      buttonText: 'Select KES 50/MO',
      buttonColor: 'purple',
      planKey: 'basic',
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 150,
      period: '/2 MO',
      features: [
        'Up to 10 Images',
        '2 Month Ad Display',
        'Enhanced ad exposure',
      ],
      maxImages: 10,
      maxVideos: 0,
      displayDays: 60,
      buttonText: 'Select KES 150/2 MO',
      buttonColor: 'purple',
      planKey: 'pro',
    },
    {
      id: 'business',
      name: 'Business',
      badge: 'BEST VALUE',
      price: 500,
      period: '/3 MO',
      features: [
        'Up to 2 Videos (Max 50MB Each) + 10 Images',
        '3 Month Ad Display',
        'Slot-based: delete & re-post free until slot expires',
      ],
      maxImages: 10,
      maxVideos: 2,
      displayDays: 90,
      buttonText: 'Select KES 500/3 MO',
      buttonColor: 'orange',
      planKey: 'business',
    },
  ];

  const handlePlanSelect = (plan, pricingInfo) => {
    // Do NOT call onClose() — PostAdFlow controls step state
    // Pass pre-resolved promo so PostAdModal doesn't need to re-fetch
    const finalPlanPrice = pricingInfo?.hasPromo
      ? (pricingInfo.finalPrice ?? plan.price)
      : plan.price;

    onPlanSelected({
      planId:        plan.id,
      planName:      plan.name,
      price:         finalPlanPrice,   // discounted price (or full price if no promo)
      originalPrice: plan.price,       // always the full price — for display strikethrough
      maxImages:     plan.maxImages,
      maxVideos:     plan.maxVideos,
      displayDays:   plan.displayDays,
      promoApplied:  pricingInfo?.hasPromo ? pricingInfo : null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>×</button>

        {/* Payment unavailable toast — shows the specific reason (server down vs tunnel off) */}
        {showPaymentToast && (
          <div className={styles.paymentToast}>
            ⚠️ {paymentBlockReason || 'Payment system is currently unavailable.'}{' '}
            Please use the <strong>Free plan</strong> for now.
          </div>
        )}
        
        <h2 className={styles.title}>Choose a Premium Plan</h2>
        <p className={styles.subtitle}>
          Pick the plan that fits your needs and start selling in Kenya's fastest-growing marketplace.
        </p>

        <div className={styles.plansGrid}>
          {plans.map((plan) => {
            const promo = plan.planKey ? promotions[plan.planKey] : null;
            const pricingInfo = promo && plan.price > 0
              ? calculatePromotionPrice(plan.price, promo, plan.planKey)
              : null;

            const hasPromo   = pricingInfo?.hasPromo === true;
            const finalPrice = hasPromo
              ? (pricingInfo.finalPrice ?? plan.price)
              : plan.price;

            // A plan requires payment only when the user actually owes money.
            // If the effective price is 0 (100% promo, free re-post, etc.) no payment
            // gateway is involved, so the button must stay active regardless of the
            // payment server / tunnel status.
            const effectivePrice  = hasPromo
              ? (pricingInfo?.isFree ? 0 : (pricingInfo?.finalPrice ?? plan.price))
              : plan.price;
            const requiresPayment = effectivePrice > 0;   // true only when money changes hands

            const isChecking = requiresPayment && !paymentCheckDone;
            const isDisabled = requiresPayment && (!paymentAvailable || !paymentCheckDone);

            return (
              <div
                key={plan.id}
                className={`${styles.planCard} ${
                  plan.id === 'free'     ? styles.planCardFree     :
                  plan.id === 'basic'   ? styles.planCardBasic    :
                  plan.id === 'pro'     ? styles.planCardPro      :
                  plan.id === 'business'? styles.planCardBusiness  : ''
                }`}
              >
                {/* Top badge — purple for POPULAR, orange for BEST VALUE */}
                {plan.badge && !hasPromo && (
                  <div className={`${styles.topBadge} ${
                    plan.id === 'business' ? styles.topBadgeOrange : styles.topBadgePurple
                  }`}>
                    {plan.badge}
                  </div>
                )}

                {/* Promotion badge (replaces top badge when active) */}
                {hasPromo && (
                  <div className={styles.promoBadge}>
                    🎁 PROMO ACTIVE
                  </div>
                )}

                <div className={styles.planHeader}>
                  <span className={styles.planIcon}>{plan.icon}</span>
                  <h3 className={styles.planName}>{plan.name}</h3>
                </div>

                <div className={styles.planPricing}>
                  {hasPromo && !pricingInfo.isFree && pricingInfo.discountAmount > 0 && (
                    <div className={styles.originalPrice}>
                      <del>KES {plan.price}</del>
                    </div>
                  )}

                  {pricingInfo?.isFree ? (
                    <div className={styles.freePrice}>
                      <span className={styles.freeBadge}>FREE</span>
                      <span className={styles.promoLabel}>🎁 {pricingInfo.label}</span>
                    </div>
                  ) : (
                    <>
                      <span className={styles.currency}>KES</span>
                      <span className={styles.price}>{finalPrice}</span>
                      <span className={styles.period}>{plan.period}</span>
                    </>
                  )}

                  {hasPromo && !pricingInfo.isFree && pricingInfo.label && (
                    <div className={styles.discountLabel}>{pricingInfo.label}</div>
                  )}
                </div>

                <ul className={styles.featuresList}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={styles.feature}>
                      <span className={styles.checkmark}>✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`${styles.selectBtn} ${
                    isDisabled
                      ? styles.selectBtnDisabled
                      : styles[`selectBtn${plan.buttonColor.charAt(0).toUpperCase() + plan.buttonColor.slice(1)}`]
                  }`}
                  disabled={isDisabled}
                  onClick={() => isDisabled ? handleUnavailableClick() : handlePlanSelect(plan, pricingInfo)}
                  title={isDisabled && !isChecking ? paymentBlockReason || 'Payment unavailable' : undefined}
                >
                  {isChecking ? (
                    '⏳ Checking…'
                  ) : isDisabled ? (
                    '🔒 Unavailable'
                  ) : pricingInfo?.isFree ? (
                    <>
                      <span className={styles.btnText}>Select</span>
                      <span className={styles.btnPromo}>🎁 FREE</span>
                    </>
                  ) : hasPromo ? (
                    <>
                      <span className={styles.btnOriginalPrice}>
                        <del>KES {plan.price}</del>
                      </span>
                      <span className={styles.btnPromoPrice}>
                        KES {finalPrice}
                      </span>
                      <span className={styles.btnPromoTag}>PROMO</span>
                    </>
                  ) : (
                    plan.buttonText
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionModal;








